import { float, floor, fract, hash } from 'three/tsl';

type N = any;

// Mixing weights for extra streams: fract(a·A + b·B) of two uniform hashes.
const MIX: [number, number][] = [
  [1, 0],
  [0, 1],
  [17.13, 31.71],
  [53.9, 7.31],
  [3.77, 91.1],
  [127.3, 11.9],
  [29.41, 61.3],
  [7.93, 157.7],
  [83.3, 43.9],
  [211.7, 5.17],
];

/**
 * Per-cell random numbers for cells of a cube-sphere node (vegetation placement).
 * `nodeData` is the node's (uc, vc, size, face). TSL's `hash` converts its seed to
 * an unsigned int, so the seed must be a non-negative integer below 2^24: node
 * coordinates are folded into a small positive range, faces are mixed in after
 * hashing, and further streams are blended from two independent hashes.
 * Returns `r(k)`, k = 0…9, each uniform in [0, 1). Layers that share a grid pass their own
 * `salt`: with the same numbers, a world's lone trees and lone boulders fell in the same cells,
 * a boulder at the foot of every tree. `world` (a non-negative integer under 15,000,000) mixes
 * in the world's seed: the same seed gives the same numbers, another seed others.
 */
export function cellRandoms(
  cell: N,
  cellsPerNode: number,
  nodeData: N,
  salt = 0,
  world = 0,
): (k: number) => N {
  const ix = floor(nodeData.x.add(1).div(nodeData.z));
  const iy = floor(nodeData.y.add(1).div(nodeData.z));
  const nodeHash = ix.mul(73).add(iy.mul(151)).mod(4093);
  const seed = float(cell).add(nodeHash.mul(cellsPerNode)).mul(2).add(world);
  const face = nodeData.w;
  const a = fract(
    hash(seed)
      .add(face.mul(0.618034))
      .add(salt * 0.7548777),
  );
  const b = fract(
    hash(seed.add(1))
      .add(face.mul(0.381966))
      .add(salt * 0.5698403),
  );
  return (k: number) => {
    const [wa, wb] = MIX[k];
    if (wb === 0) return a;
    if (wa === 0) return b;
    return fract(a.mul(wa).add(b.mul(wb)));
  };
}
